import { useEffect, useState } from "react";
import { Modal } from "../common/Modal";
import { Button } from "../common/Button";
import { sitesApi } from "../../services/sitesApi";

/**
 * What the role you just accepted actually means (#308).
 *
 * Most people have never looked at the team features the first time
 * somebody adds them to an account, so the role name on its own tells them
 * nothing -- and "Limited Editor (site-specific)" tells them less than
 * nothing, because it sounds like a restriction without saying which.
 *
 * Shown at the moment of ACCEPTING rather than on the dashboard banner:
 * the banner deliberately links through instead of accepting inline, so
 * this belongs where the decision actually lands.
 *
 * EVERY LINE HERE MUST BE TRUE. This was blocked on #307 for exactly that
 * reason -- until the read/write rule was enforced outside the team page,
 * "you can view but not change" was false, and a popup that misstates
 * permissions is worse than none because it is what people rely on
 * instead of testing.
 */
const ROLES = {
  Admin: {
    title: "Admin",
    can: [
      "Add and edit hives, log inspections, treatments and feeding",
      "Record harvests, move honey and handle vendors and sales",
      "Assign and delete team tasks, and remove anyone's comments",
    ],
    cannot: [
      "Change the account's subscription or billing",
      "Invite people to the account, or change what other members can do",
    ],
  },
  Editor: {
    title: "Editor",
    can: [
      "Add and edit hives, log inspections, treatments and feeding",
      "Record harvests, move honey and handle vendors and sales",
      "Add team tasks and comments, and delete your own",
    ],
    cannot: [
      "Delete other people's tasks or comments",
      "Invite people, change roles, or touch the subscription",
    ],
  },
  Contributor: {
    title: "Contributor",
    can: [
      "Log inspections, treatments and feeding, record harvests, bottling and sales",
      "Add team tasks and comments, and delete your own",
    ],
    cannot: [
      "Add, rename, put away or merge varietals, bottle sizes, equipment types or list options",
      "Merge vendors or hives, invite people, change roles, or touch the subscription",
    ],
  },
  Viewer: {
    title: "Viewer",
    can: [
      "See the hives, their history, inspections and health",
      "Read team tasks and comments",
    ],
    cannot: [
      "Change anything at all — every save is refused, not hidden",
      "Log an inspection, record a harvest, or enter a sale",
    ],
  },
  Limited: {
    title: "Limited Editor",
    can: [
      "Do everything an Editor can, but only at the sites you were given",
      "Log inspections, treatments and feeding at those sites",
    ],
    cannot: [
      "See or touch hives at any other site — they simply will not appear",
      "Invite people, change roles, or touch the subscription",
    ],
  },
};

export function RoleExplainer({ share, onClose }) {
  const [sites, setSites] = useState(null);
  const info = ROLES[share?.role];

  useEffect(() => {
    if (share?.role !== "Limited") return;
    let cancelled = false;
    // The member's own view: /sites is RLS-scoped, so for a Limited member
    // it returns exactly the sites they were given. Naming them is the
    // whole point of the role and is invisible otherwise.
    sitesApi
      .list()
      .then((r) => !cancelled && setSites(r.sites || []))
      // Best-effort. The generic sentence below is still true without it,
      // and a failed lookup must not swallow the explanation.
      .catch(() => !cancelled && setSites([]));
    return () => { cancelled = true; };
  }, [share]);

  if (!info) return null;

  const until = share.access_until;
  const from = share.access_from;

  return (
    <Modal open onClose={onClose} title={`You're now ${aOrAn(info.title)}`}
      footer={<Button onClick={onClose}>Got it</Button>}>
      <p style={{ marginTop: 0 }}>
        <strong>{share.primary_email}</strong> gave you the{" "}
        <strong>{info.title}</strong> role on their apiary. Here's what that
        means day to day.
      </p>

      <Section title="You can" items={info.can} />
      <Section title="You can't" items={info.cannot} muted />

      {share.role === "Limited" && sites !== null ? (
        <p style={{ color: "var(--color-text-muted)" }}>
          {sites.length
            ? <>Your sites: <strong>{sites.map((s) => s.dock_name).join(", ")}</strong>.</>
            : <>No sites have been assigned to you yet — ask {share.primary_email} to add some, or you'll see an empty app.</>}
        </p>
      ) : null}

      {/* Money is a separate switch from the role (V051), so it has to be
          said separately or a member who cannot see prices reports it as a
          bug. */}
      <p style={{ color: "var(--color-text-muted)" }}>
        {share.can_see_financials
          ? "You can see prices, sales and what customers owe."
          : "Money is hidden from you — prices, sales and balances won't appear. That's a setting the owner controls."}
      </p>

      {until ? (
        // Access silently stopping is the worst way to find out it was
        // temporary.
        <p style={{ color: "var(--color-warning)" }}>
          This access runs {from ? `from ${fmt(from)} ` : ""}until <strong>{fmt(until)}</strong>.
          After that you'll need the owner to extend it.
        </p>
      ) : null}
    </Modal>
  );
}

function Section({ title, items, muted = false }) {
  return (
    <div style={{ marginBottom: "var(--space-3)" }}>
      <strong style={{ color: muted ? "var(--color-text-muted)" : undefined }}>{title}</strong>
      <ul style={{ margin: "var(--space-1) 0 0", paddingLeft: "1.2rem" }}>
        {items.map((t) => <li key={t} style={{ color: muted ? "var(--color-text-muted)" : undefined }}>{t}</li>)}
      </ul>
    </div>
  );
}

function aOrAn(word) {
  return /^[AEIOU]/i.test(word) ? `an ${word}` : `a ${word}`;
}

function fmt(d) {
  return new Date(d).toLocaleDateString();
}
